import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getGameData } from 'core/selectors';
import { startRoundAction, startDrawAction, confirmResultAction } from 'data/reducers';

import { TableStates, ResultBall } from 'types';
import { IEnabledButton } from 'types/common';
import { GameType } from 'types/tables';

type ButtonControlsHookType = {
  enabledButtons: IEnabledButton;
  isChangeDealerEnabled: boolean;
  onOpenRoundClick: () => void;
  onStartDrawClick: () => void;
  onConfirmResultClick: () => void;
};

export const useButtonControls = (): ButtonControlsHookType => {
  const dispatch = useDispatch();

  const {
    auto_confirm_result: autoConfirmResult,
    auto_draw: autoDraw,
    auto_round: autoRound,
    tableState,
    round: { drawResults, winner },
    game_type,
    rng,
  } = useSelector(getGameData);

  const isButtonConfirmResultEnabled = useMemo(
    () =>
      autoConfirmResult
        ? false
        : !(tableState !== TableStates.DrawFinished || winner === ResultBall.UNDEFINED),
    [winner, tableState, autoConfirmResult],
  );

  const isButtonChangeResultEnabled = useMemo(() => {
    if (game_type === GameType.ROULETTE) {
      return false;
    }
    if (rng) {
      return false;
    }
    return tableState === TableStates.DrawFinished || tableState === TableStates.DrawStarted;
  }, [tableState, game_type, rng]);

  const isButtonCancelRoundEnabled = useMemo(() => {
    if (game_type === GameType.ROULETTE) {
      return false;
    }
    return (
      tableState === TableStates.RoundStarted ||
      tableState === TableStates.BettingTimeStarted ||
      tableState === TableStates.BettingTimeFinished ||
      tableState === TableStates.DrawStarted ||
      tableState === TableStates.DrawFinished
    );
  }, [tableState, game_type]);

  const enabledButtonDuringRounds = useMemo(
    () =>
      tableState === TableStates.TableOpened ||
      tableState === TableStates.RoundFinished ||
      tableState === TableStates.RoundCanceled,
    [tableState],
  );

  const enabledButtonAfterCancelRound = useMemo(
    () => tableState === TableStates.RoundCanceled,
    [tableState],
  );

  const enabledButtonFirstOpen = useMemo(
    () => tableState === TableStates.TableOpened || tableState === TableStates.RoundCanceled,
    [tableState],
  );

  const isButtonOpenRoundEnabled = useMemo(() => {
    if (game_type === GameType.ROULETTE) {
      return false;
    }
    if (autoRound) {
      if (game_type === GameType.CLASSIC_BAC) {
        return rng ? enabledButtonFirstOpen : enabledButtonDuringRounds;
      }

      if (drawResults.length) {
        return enabledButtonAfterCancelRound;
      }
      return enabledButtonDuringRounds;
    }
    return enabledButtonDuringRounds;
  }, [
    autoRound,
    drawResults,
    enabledButtonDuringRounds,
    enabledButtonAfterCancelRound,
    enabledButtonFirstOpen,
    game_type,
    rng,
  ]);

  const isButtonStartDrawEnabled = useMemo(
    () => (autoDraw ? false : tableState === TableStates.BettingTimeFinished),
    [tableState, autoDraw],
  );

  const isChangeDealerEnabled = useMemo(
    () =>
      tableState === TableStates.TableOpened ||
      tableState === TableStates.RoundFinished ||
      tableState === TableStates.BettingTimeStarted ||
      tableState === TableStates.RoundStarted ||
      tableState === TableStates.RoundCanceled,
    [tableState],
  );

  const onOpenRoundClick = useCallback(() => {
    dispatch(startRoundAction());
  }, [dispatch]);

  const onStartDrawClick = useCallback(() => {
    dispatch(startDrawAction());
  }, [dispatch]);

  const onConfirmResultClick = useCallback(() => {
    dispatch(confirmResultAction());
  }, [dispatch]);

  return {
    enabledButtons: {
      isButtonConfirmResultEnabled,
      isButtonChangeResultEnabled,
      isButtonCancelRoundEnabled,
      isButtonOpenRoundEnabled,
      isButtonStartDrawEnabled,
    },
    isChangeDealerEnabled,
    onOpenRoundClick,
    onStartDrawClick,
    onConfirmResultClick,
  };
};
